/**
 * The persistent application frame: sidebar, header, and the routed page.
 *
 * The shell never scrolls. Only <main> does, and inside a master-detail view
 * only the individual panes do - the brief forbids the whole page moving.
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { healthApi } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.jsx';
import {
  IconCatalogue,
  IconKey,
  IconLog,
  IconLogout,
  IconRegistry,
  IconSettings,
  IconStar,
} from './Icons.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import { ShortlistButton, ShortlistPanel } from './ShortlistPanel.jsx';
import TooltipLayer from './TooltipLayer.jsx';

/**
 * Nav entries. `title` also drives the header, so a route rename happens in
 * exactly one place.
 */
const NAV_ITEMS = [
  {
    to: '/catalogue',
    label: 'Catalogue',
    title: 'SIC catalogue',
    subtitle: 'Find the codes for your market, then acquire the companies that file them',
    Icon: IconCatalogue,
  },
  {
    to: '/registry',
    label: 'Registry',
    title: 'Company registry',
    subtitle: 'Every company acquired so far',
    Icon: IconRegistry,
  },
  {
    to: '/lists',
    label: 'Lists',
    title: 'Favorites and lists',
    subtitle: 'Companies you have starred or organised into named lists',
    Icon: IconStar,
  },
  {
    to: '/log',
    label: 'Acquisition log',
    title: 'Acquisition log',
    subtitle: 'A permanent record of every run',
    Icon: IconLog,
  },
  {
    to: '/api-keys',
    label: 'API keys',
    title: 'API keys',
    subtitle: 'Enter a provider key and test the connection',
    Icon: IconKey,
  },
  {
    to: '/settings',
    label: 'Settings',
    title: 'Settings',
    subtitle: 'Connection status and configuration',
    Icon: IconSettings,
  },
];

/**
 * Health indicator in the header. Polls slowly - this answers "is the backend
 * still there", not "how is my acquisition doing", which polls at 2s.
 */
function HealthDot() {
  const [health, setHealth] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const poll = () => {
      healthApi
        .check()
        .then((data) => {
          if (cancelled) return;
          setHealth(data);
          setFailed(false);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    };

    poll();
    const timer = setInterval(poll, 30_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  if (failed) {
    return (
      <span
        className="badge badge-error tip tip-below"
        data-tip="The web client cannot reach the API. Check that the API process is running on port 5191."
      >
        <span className="badge-dot" />
        API offline
      </span>
    );
  }

  if (!health) {
    return (
      <span className="badge">
        <span className="badge-dot" />
        Checking
      </span>
    );
  }

  const dbOk = health.checks?.database?.connected;

  return (
    <span
      className={`badge ${dbOk ? 'badge-ok' : 'badge-error'} tip tip-below`}
      data-tip={
        dbOk
          ? `API ${health.version} - ${health.phase}. Database responding in ${health.checks.database.latencyMs}ms.`
          : `The API is running but cannot reach MySQL: ${health.checks?.database?.error ?? 'unknown error'}`
      }
    >
      <span className="badge-dot" />
      {dbOk ? 'Healthy' : 'Database down'}
    </span>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const location = useLocation();
  // The shortlist lives in the shell rather than on the catalogue page: its
  // count is a persistent header fixture, and phase 4 opens the acquisition
  // dialog from it on any screen.
  const [isShortlistOpen, setIsShortlistOpen] = useState(false);

  const current =
    NAV_ITEMS.find((item) => location.pathname.startsWith(item.to)) ?? NAV_ITEMS[0];

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="sidebar-brand-mark">LS</span>
          <span className="sidebar-brand-text">
            <span className="sidebar-brand-name">Leads Scanner</span>
            <span className="sidebar-brand-sub">UK company intelligence</span>
          </span>
        </div>

        <nav className="sidebar-nav" aria-label="Main">
          <div className="sidebar-section-label">Workspace</div>
          {NAV_ITEMS.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-item${isActive ? ' is-active' : ''}`}
            >
              <span className="nav-item-icon">
                <Icon />
              </span>
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

      </aside>

      <header className="header">
        <div>
          <div className="header-title">{current.title}</div>
        </div>
        <div className="header-subtitle">{current.subtitle}</div>

        <div className="header-actions">
          <ShortlistButton onOpen={() => setIsShortlistOpen(true)} />

          <span className="header-divider" />

          <HealthDot />

          <span className="header-divider" />

          <ThemeToggle className="btn btn-ghost btn-icon tip tip-below tip-left" />

          <span className="header-divider" />

          <span className="text-xs text-faint">{user?.email}</span>

          <button
            type="button"
            className="btn btn-ghost btn-icon tip tip-below tip-left"
            onClick={() => void logout()}
            data-tip="Sign out"
            aria-label="Sign out"
          >
            <IconLogout />
          </button>
        </div>
      </header>

      <main className="main">
        <Outlet />
      </main>

      {isShortlistOpen ? <ShortlistPanel onClose={() => setIsShortlistOpen(false)} /> : null}

      <TooltipLayer />
    </div>
  );
}
